import { VenoStorm__factory } from '@contracts';
import { BigNumber } from '@ethersproject/bignumber';
import { VenoSDKModule } from './base';
import { BLOCKS_PER_YEAR, expandDecimals } from './constants';
import FerroLpTokenModule from './ferroLpToken';
import FerroLpTokenAtomModule from './ferroLpTokenAtom';
import LiquidCroModule from './liquidCro';
import TLcroModule from './tLcro';
import { getCroUsdPrice, getDecimalsForType, getPid, getTokenContract, getVnoUsdPrice, wrapSdkTransaction, } from './util';
import VvsCroLpTokenModule from './vvsCroLpToken';
import VvsLpTokenModule from './vvsLpToken';
export default class VenoStormModule extends VenoSDKModule {
    constructor(sdk) {
        super(sdk);
        this.contractFactory = VenoStorm__factory;
        this.contractAddress = this.sdk.addresses.venoStorm;
        this.TLcro = new TLcroModule(this.sdk);
        this.FerroLpToken = new FerroLpTokenModule(this.sdk);
        this.FerroLpTokenAtom = new FerroLpTokenAtomModule(this.sdk);
        this.VvsLpToken = new VvsLpTokenModule(this.sdk);
        this.VvsCroLpToken = new VvsCroLpTokenModule(this.sdk);
        this.LiquidCro = new LiquidCroModule(this.sdk);
    }
    vnoPerBlock() {
        return this.getContract().vnoPerBlock();
    }
    totalAllocPoint() {
        return this.getContract().totalAllocPoint();
    }
    async fountainAllocPoint() {
        const pool = await this.getContract().poolInfo(0);
        return pool.allocPoint;
    }
    async vnoRewardPerBlock() {
        const [vnoPerBlock, totalAllocPoint, fountainAllocPoint] = await Promise.all([
            this.vnoPerBlock(),
            this.totalAllocPoint(),
            this.fountainAllocPoint(),
        ]);
        return vnoPerBlock.mul(fountainAllocPoint).div(totalAllocPoint);
    }
    async stakeNft(type, collections, tokenIds) {
        const collectionAddresses = collections.map((collection) => getTokenContract(collection, this.addresses));
        const nfts = tokenIds.map((tokenId, i) => ({
            tokenContract: collectionAddresses[i],
            tokenId,
        }));
        return await wrapSdkTransaction(this.getContract().stakeNft(getPid(type), nfts));
    }
    async unstakeNft(type, collections, tokenIds) {
        const collectionAddresses = collections.map((collection) => getTokenContract(collection, this.addresses));
        const nfts = tokenIds.map((tokenId, i) => ({
            tokenContract: collectionAddresses[i],
            tokenId,
        }));
        return await wrapSdkTransaction(this.getContract().unstakeNft(getPid(type), nfts));
    }
    async getStakedNftTokenIds(type, collection, user, useVersion2) {
        const tokenContract = getTokenContract(collection, this.addresses);
        if (!useVersion2) {
            return this.getStakedNftTokenIdsV1(type, collection, user);
        }
        try {
            return (await this.getContract().getNftStakedByAddress(getPid(type), user, tokenContract)).map((tokenId) => tokenId.toNumber());
        }
        catch (error) {
            // backward compatible
            console.log('getStakedNftTokenIds', error);
            return this.getStakedNftTokenIdsV1(type, collection, user);
        }
    }
    /**
     * @deprecated
     * todo cover.lv remove this method after contract side is updated
     */
    async getStakedNftTokenIdsV1(type, collection, user) {
        const tokenContract = getTokenContract(collection, this.addresses);
        try {
            return (await this.getContract().getNftStaked(getPid(type), user))
                .filter((nft) => nft.tokenContract === tokenContract)
                .map((nft) => nft.tokenId.toNumber());
        }
        catch (error) {
            return [];
        }
    }
    async getBoostMultiplier(type, user) {
        return ((await this.getContract().getBoostMultiplier(getPid(type), user)).toNumber() / 10000);
    }
    async deposit(type, amount, contractAddress) {
        return await wrapSdkTransaction(this.getContract().deposit(getPid(type), amount, contractAddress));
    }
    async withdraw(type, amount, contractAddress) {
        return await wrapSdkTransaction(this.getContract().withdraw(getPid(type), amount, contractAddress));
    }
    async getPendingVno(type, user) {
        return await this.getContract().pendingVno(getPid(type), user);
    }
    async getLpAmountStaked(type, user) {
        const [amount] = await this.getContract().userInfo(getPid(type), user);
        return amount;
    }
    async getGardenAprV2(type, user) {
        const gardenType = type === 'FERRO_ATOM' ? 'FERRO' : type;
        const factor = 100000000000;
        const [{ allocPoint, vnoPerBlock, totalAllocPoint, vnoUsdPrice, totalBoostedAmount, }, boostMultiplier, oneLpUsdValue,] = await Promise.all([
            this.getDataForApr(type),
            user
                ? this.getContract().getBoostMultiplier(getPid(type), user)
                : (() => 10000)(),
            this.getLpValueUsdV2(type, BigNumber.from(expandDecimals(1, getDecimalsForType(gardenType)))),
        ]);
        // something went wrong, be safe and return 0
        if (!oneLpUsdValue ||
            oneLpUsdValue.lte(0) ||
            totalBoostedAmount.lte(0) ||
            totalAllocPoint.lte(0)) {
            return 0;
        }
        const apr = allocPoint
            .mul(vnoPerBlock)
            .mul(BLOCKS_PER_YEAR)
            .mul(Math.round(factor * (vnoUsdPrice ?? 0)))
            .mul(boostMultiplier)
            // make sure its scaled correctly for coins like t tokens
            .div(expandDecimals(1, 18 - getDecimalsForType(gardenType)))
            .div(totalBoostedAmount)
            .div(totalAllocPoint)
            .mul(expandDecimals(1, getDecimalsForType(gardenType)))
            .div(oneLpUsdValue)
            .toNumber() / factor;
        return apr;
    }
    async getDataForApr(type) {
        const pid = getPid(type);
        let lpStakedPromise = (async () => BigNumber.from(0))();
        if (type === 'TECTONIC') {
            // for Tectonic, save calls/calculations by getting value in LCRO terms
            lpStakedPromise = this.TLcro.balanceOfUnderlying(this.getContract().address);
        }
        else if (type === 'FERRO') {
            lpStakedPromise = this.FerroLpToken.balanceOf(this.getContract().address);
        }
        else if (type === 'FERRO_ATOM') {
            lpStakedPromise = this.FerroLpTokenAtom.balanceOf(this.getContract().address);
        }
        else if (type === 'VVS') {
            lpStakedPromise = this.VvsLpToken.balanceOf(this.getContract().address);
        }
        else if (type === 'VVS_CRO') {
            lpStakedPromise = this.VvsCroLpToken.balanceOf(this.getContract().address);
        }
        const [[, allocPoint, , , , totalBoostedAmount], vnoPerBlock, totalAllocPoint, lpStaked, vnoUsdPrice,] = await Promise.all([
            this.getContract().poolInfo(pid),
            this.getContract().vnoPerBlock(),
            this.getContract().totalAllocPoint(),
            lpStakedPromise,
            getVnoUsdPrice(),
        ]);
        const lpValueUsd = await this.getLpValueUsd(type, lpStaked);
        return {
            allocPoint,
            vnoPerBlock,
            totalAllocPoint,
            vnoUsdPrice,
            lpValueUsd,
            totalBoostedAmount,
            lpStaked,
        };
    }
    async getLpValueUsd(type, lpStaked) {
        let lpValueUsdPromise = (async () => BigNumber.from(0))();
        if (type === 'FERRO') {
            lpValueUsdPromise = this.FerroLpToken.getLpUsdWorth(lpStaked);
        }
        else if (type === 'FERRO_ATOM') {
            lpValueUsdPromise = this.FerroLpTokenAtom.getLpUsdWorth(lpStaked);
        }
        else if (type === 'VVS') {
            lpValueUsdPromise = this.VvsLpToken.getLpUsdWorth(lpStaked);
        }
        else if (type === 'VVS_CRO') {
            lpValueUsdPromise = this.VvsCroLpToken.getLpUsdWorth(lpStaked);
        }
        else if (type === 'TECTONIC') {
            // For Tectonic, we got underlying (LCRO) value (see above)
            lpValueUsdPromise = this.LiquidCro.getUsdWorth(lpStaked);
        }
        const lpValueUsd = await lpValueUsdPromise;
        return lpValueUsd;
    }
    async getLpValueUsdV2(type, lpStaked) {
        let lpValueUsdPromise = (async () => BigNumber.from(0))();
        if (type === 'FERRO') {
            lpValueUsdPromise = this.FerroLpToken.getLpUsdWorth(lpStaked);
        }
        else if (type === 'FERRO_ATOM') {
            lpValueUsdPromise = this.FerroLpTokenAtom.getLpUsdWorth(lpStaked);
        }
        else if (type === 'VVS') {
            lpValueUsdPromise = this.VvsLpToken.getLpUsdWorth(lpStaked);
        }
        else if (type === 'VVS_CRO') {
            lpValueUsdPromise = this.VvsCroLpToken.getLpUsdWorth(lpStaked);
        }
        else if (type === 'TECTONIC') {
            const [exchangeRate, croUsdPrice] = await Promise.all([
                this.TLcro.getExchangeRate(),
                getCroUsdPrice(),
            ]);
            const tLcroPerUsd = BigNumber.from(10)
                .pow(28)
                .mul(100000000)
                .div(exchangeRate)
                .toNumber() /
                ((croUsdPrice ?? 1) * 100000000);
            const usdPerTlcro = 1 / tLcroPerUsd;
            const result = lpStaked
                .mul(Math.round(usdPerTlcro * 100000000))
                .div(100000000);
            return result;
        }
        const lpValueUsd = await lpValueUsdPromise;
        return lpValueUsd;
    }
    async getGardenApr(type) {
        const { allocPoint, vnoPerBlock, totalAllocPoint, lpValueUsd, vnoUsdPrice, } = await this.getDataForApr(type);
        // something went wrong, be safe and return 0
        if (!lpValueUsd || lpValueUsd.lte(0) || !vnoUsdPrice)
            return 0;
        const factor = 100000000000;
        const result = allocPoint
            .mul(vnoPerBlock)
            .mul(BLOCKS_PER_YEAR)
            .mul(Math.round(factor * vnoUsdPrice))
            .div(totalAllocPoint.mul(lpValueUsd))
            .toNumber() / factor;
        return result;
    }
    async rarityRegistries(collection) {
        const contractAddress = getTokenContract(collection, this.addresses);
        return this.getContract().rarityRegistries(contractAddress);
    }
}
